import * as React from 'react';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { Backdrop } from '@mui/material';

interface AppFwBottomSheetProps {


    /**
     * Whether the sheet is open.
     */
    readonly open: boolean;

    /**
     * Callback when open state changes.
     */
    readonly onOpenChange: (open: boolean) => void;

    /**
     * The single content of the sheet.
     */
    readonly children: React.ReactNode;
}

const Puller = styled(Box)(({ theme }) => ({
    width: 30,
    height: 6,
    backgroundColor: theme.palette.mode === 'light' ? grey[300] : grey[900],
    borderRadius: 3,
    position: 'absolute',
    top: 8,
    left: 'calc(50% - 15px)',
}));

export default function AppFwBottomSheet(props: AppFwBottomSheetProps) {
    const {
        open,
        onOpenChange,
        children
    } = props;

    // Use a ref to measure full content height if needed, 
    // but for now max-height animation usually works well enough or we rely on '100%' within constraints.
    // CSS Grid transition trick is also popular for height: auto, but let's try max-height first or fixed heights.
    // Users asked for "fixe Aktion", so we can just animate to a large max-height.

    const toggle = () => {
        onOpenChange(!open);
    };

    // Swipe Handling
    const touchStartY = React.useRef<number | null>(null);
    const touchStartX = React.useRef<number | null>(null);
    const scrollContentRef = React.useRef<HTMLDivElement>(null);

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartY.current = e.targetTouches[0].clientY;
        touchStartX.current = e.targetTouches[0].clientX;
    };

    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartY.current === null || touchStartX.current === null) return;

        const touchEndY = e.changedTouches[0].clientY;
        const touchEndX = e.changedTouches[0].clientX;

        const dy = touchEndY - touchStartY.current;
        const dx = touchEndX - touchStartX.current;

        // Reset
        touchStartY.current = null;
        touchStartX.current = null;

        // Basic swipe detection
        const minSwipeDistance = 50;
        const isVertical = Math.abs(dy) > Math.abs(dx);

        if (isVertical && Math.abs(dy) > minSwipeDistance) {
            if (dy < 0) {
                // Swipe Up
                if (!open) {
                    onOpenChange(true);
                }
            } else {
                // Swipe Down
                if (open) {
                    // Only close if we are not scrolling content
                    // Check if content is scrolled to top
                    if (scrollContentRef.current && scrollContentRef.current.scrollTop > 0) {
                        return;
                    }
                    onOpenChange(false);
                }
            }
        }
    };

    return (
        <>
            <Backdrop
                open={open}
                onClick={() => onOpenChange(false)}
                sx={{ zIndex: 1100 }}
            />
            <Paper
                elevation={3}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    overflow: 'hidden',
                    zIndex: 1101, // Above backdrop
                    height: 'auto',
                    maxHeight: open ? '90dvh' : 'none',
                    transition: 'all 0.3s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header / Puller Area */}
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        pt: 1,
                        pb: 2,
                        cursor: 'pointer',
                        flexShrink: 0
                    }}
                    onClick={toggle}
                >
                    <Puller />
                </Box>

                {/* Content Area */}
                <Box
                    ref={scrollContentRef}
                    sx={{
                        px: 2,
                        pb: 2,
                        overflowY: open ? 'auto' : 'hidden',
                        flex: 1
                    }}
                >
                    {children}
                </Box>
            </Paper>
        </>
    );
}
